import type { ReactNode } from 'react';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';

// A trigger's rect at click time: left/right edges, plus which edge a right-aligned menu hangs from.
export interface MenuAnchor {
  x: number;
  y: number;
  top?: number;
  right?: number;
  align?: 'start' | 'end';
}

// A popover anchored to a rect snapshot, for triggers with no React element of their own (e.g. ProseMirror).
export function AnchoredPopover({
  anchor,
  onClose,
  className,
  keepFocus = false,
  children
}: {
  anchor: MenuAnchor;
  onClose: () => void;
  className?: string;
  // Never take focus on mousedown; needed when the menu floats over an editor whose caret must not move.
  keepFocus?: boolean;
  children: ReactNode;
}) {
  const { x, y, top, right, align } = anchor;
  return (
    <Popover open onOpenChange={(open) => !open && onClose()}>
      <PopoverAnchor asChild>
        <span
          style={{
            position: 'fixed',
            left: x,
            top: top ?? y,
            width: (right ?? x) - x,
            height: top !== undefined ? y - top : 0
          }}
        />
      </PopoverAnchor>
      <PopoverContent
        align={align === 'end' ? 'end' : 'start'}
        sideOffset={4}
        collisionPadding={8}
        className={className}
        // A trigger click that reopens the same menu must not be eaten by outside-click dismissal.
        onPointerDownOutside={(event) => {
          if ((event.target as Element).closest?.('[data-menu-trigger]')) event.preventDefault();
        }}
        onOpenAutoFocus={(event) => event.preventDefault()}
        onMouseDown={keepFocus ? (event) => event.preventDefault() : undefined}
      >
        {children}
      </PopoverContent>
    </Popover>
  );
}
